export interface LedgerSafeViewportAnchor {
  rowId: string | null
  rowOffsetTop: number | null
  scrollTop: number
  scrollLeft: number
}

type LedgerSafeViewportAnchorCaptureOptions = {
  viewport: HTMLElement
  orderedRowIds: string[]
  rowElements: ReadonlyMap<string, HTMLElement>
  preferredRowIds?: string[]
}

type LedgerSafeViewportAnchorRestoreOptions = {
  viewport: HTMLElement
  anchor: LedgerSafeViewportAnchor
  rowElements: ReadonlyMap<string, HTMLElement>
  fallbackRowIds?: string[]
}

const uniqueIds = (values: string[]): string[] => Array.from(new Set(values.filter(Boolean)))

const isVisibleWithin = (element: HTMLElement, viewportRect: DOMRect): boolean => {
  const rect = element.getBoundingClientRect()
  return rect.bottom > viewportRect.top && rect.top < viewportRect.bottom
}

export const captureLedgerSafeViewportAnchor = ({
  viewport,
  orderedRowIds,
  rowElements,
  preferredRowIds = [],
}: LedgerSafeViewportAnchorCaptureOptions): LedgerSafeViewportAnchor => {
  const viewportRect = viewport.getBoundingClientRect()
  const candidateIds = uniqueIds([...preferredRowIds, ...orderedRowIds])
  const rowId = candidateIds.find(candidateId => {
    const element = rowElements.get(candidateId)
    return Boolean(element && isVisibleWithin(element, viewportRect))
  }) || null
  const rowElement = rowId ? rowElements.get(rowId) : null

  return {
    rowId,
    rowOffsetTop: rowElement
      ? rowElement.getBoundingClientRect().top - viewportRect.top
      : null,
    scrollTop: viewport.scrollTop,
    scrollLeft: viewport.scrollLeft,
  }
}

export const restoreLedgerSafeViewportAnchor = ({
  viewport,
  anchor,
  rowElements,
  fallbackRowIds = [],
}: LedgerSafeViewportAnchorRestoreOptions): boolean => {
  const targetRowId = uniqueIds([
    ...(anchor.rowId ? [anchor.rowId] : []),
    ...fallbackRowIds,
  ]).find(rowId => rowElements.has(rowId))

  viewport.scrollLeft = anchor.scrollLeft
  if (!targetRowId || anchor.rowOffsetTop === null) {
    viewport.scrollTop = anchor.scrollTop
    return false
  }

  const rowElement = rowElements.get(targetRowId)
  if (!rowElement) {
    viewport.scrollTop = anchor.scrollTop
    return false
  }

  const viewportTop = viewport.getBoundingClientRect().top
  const currentOffsetTop = rowElement.getBoundingClientRect().top - viewportTop
  viewport.scrollTop += currentOffsetTop - anchor.rowOffsetTop
  return true
}
